import { create } from 'zustand';

// ─── Store-Typen ────────────────────────────────────────────────────────────

export type ViewMode = 'tree' | 'map';

interface TreeStore {
  /** Aktuelle Ansicht: Stammbaum oder Karte */
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;

  /** ID der aktuell ausgewählten Person (für PersonPanel-Sidebar) */
  selectedPersonId: string | null;
  /**
   * Person auswählen. Standardmäßig öffnet sich dabei das Detail-Panel;
   * `openPanel: false` bewegt nur den Fokus im Baum (Tastatur-Navigation),
   * ohne ein geschlossenes Panel aufzuklappen.
   */
  setSelectedPerson: (id: string | null, options?: { openPanel?: boolean }) => void;

  /** Steuert die Sichtbarkeit der Sidebar */
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;

  /** Generationen-Tiefe: null = alle anzeigen, Zahl = N Generationen um ausgewählte Person */
  generationDepth: number | null;
  setGenerationDepth: (depth: number | null) => void;

  /**
   * Fokus-Person für „Eigene Linie zentrieren". Ist sie gesetzt, wird ihre
   * direkte Vorfahren-Kette (über primaryParentId) zu einer senkrechten Achse;
   * andere Zweige weichen zur Seite. null = aus.
   */
  lineageFocusId: string | null;
  setLineageFocus: (id: string | null) => void;

  /** Farbige Familien-Linien an/aus */
  coloredLines: boolean;
  setColoredLines: (on: boolean) => void;

  /** Aktives Medizin-Overlay (Knoten nach Krankheits-/Trägerstatus einfärben). null = aus. */
  activeOverlay: { catalogId: string; label: string } | null;
  setActiveOverlay: (overlay: { catalogId: string; label: string } | null) => void;

  /** Zeit-Schieberegler: ausgewähltes Jahr (Verstorbene ausgegraut, Ungeborene ausgeblendet). null = aus. */
  timeYear: number | null;
  setTimeYear: (year: number | null) => void;

  /** Übersichtskarte (Minimap) unten rechts einblenden. */
  minimapOpen: boolean;
  setMinimapOpen: (open: boolean) => void;

  /**
   * Erkundungsansicht: statt des ganzen Baums nur eine Person und ihre
   * Umgebung, die man Schritt für Schritt aufdeckt.
   *
   * Muss AUSDRÜCKLICH eingeschaltet werden und bleibt es nur für diese
   * Sitzung. Wer seinen Baum kennt und ihn ganz sehen will, soll ihn ganz
   * sehen – auch mit achthundert Personen. Die Anwendung darf einem
   * gepflegten Stammbaum nicht unterstellen, er sei zu groß.
   */
  erkundung: boolean;
  setErkundung: (an: boolean) => void;

  /** Die bereits aufgedeckten Personen. Leer = noch nicht begonnen. */
  aufgedeckt: Set<string>;
  setAufgedeckt: (ids: Set<string>) => void;

  /**
   * Sprungwunsch aus einer der Navbar-Suchen: die Baumansicht fährt zu dieser
   * Person, sobald das Layout steht, und löscht den Wunsch danach wieder.
   */
  focusRequestId: string | null;
  requestFocus: (id: string) => void;
  clearFocusRequest: () => void;

}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useTreeStore = create<TreeStore>((set) => ({
  viewMode: 'tree' as ViewMode,
  setViewMode: (mode) => set({ viewMode: mode }),

  selectedPersonId: null,
  setSelectedPerson: (id, options) =>
    set((state) => ({
      selectedPersonId: id,
      // Sidebar automatisch öffnen wenn Person gewählt, schließen wenn null.
      // Bei openPanel:false bleibt der bisherige Zustand erhalten – ein
      // offenes Panel folgt der Auswahl, ein geschlossenes bleibt zu.
      sidebarOpen:
        id === null ? false : (options?.openPanel ?? true) ? true : state.sidebarOpen,
    })),

  sidebarOpen: false,
  setSidebarOpen: (open) =>
    set({
      sidebarOpen: open,
      // Wenn Sidebar geschlossen wird, Auswahl aufheben
      ...(open ? {} : { selectedPersonId: null }),
    }),

  generationDepth: null,
  setGenerationDepth: (depth) => set({ generationDepth: depth }),

  lineageFocusId: null,
  setLineageFocus: (id) => set({ lineageFocusId: id }),

  coloredLines: true,
  setColoredLines: (on) => set({ coloredLines: on }),

  activeOverlay: null,
  setActiveOverlay: (overlay) => set({ activeOverlay: overlay }),

  timeYear: null,
  setTimeYear: (year) => set({ timeYear: year }),

  minimapOpen: false,
  setMinimapOpen: (open) => set({ minimapOpen: open }),

  erkundung: false,
  setErkundung: (an) =>
    // Beim Ausschalten die aufgedeckten Personen vergessen: wer zurück in die
    // Gesamtansicht geht und später wieder erkundet, fängt neu an – und nicht
    // mitten in einem Zwischenstand, an den er sich nicht erinnert.
    set(an ? { erkundung: true } : { erkundung: false, aufgedeckt: new Set<string>() }),

  aufgedeckt: new Set<string>(),
  setAufgedeckt: (ids) => set({ aufgedeckt: ids }),

  focusRequestId: null,
  requestFocus: (id) => set({ focusRequestId: id }),
  clearFocusRequest: () => set({ focusRequestId: null }),

}));
